// CreateEmployeeModal.js
import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  Upload,
  message,
  Radio,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createUserFx } from "../../models/userListModel/effects";
import { useStoreMap } from "effector-react";
import { $userGroups } from "../../models/userModel/stores";

import styles from "./EmployeeUserList.module.css";

const { Option } = Select;

export default function CreateEmployeeModal({ visible, onClose }) {
  const [form] = Form.useForm();
  const [photoBase64, setPhotoBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const userGroups = useStoreMap({
    store: $userGroups,
    keys: [],
    fn: (groups) => groups || [],
  });

  const isAzag = userGroups.includes("Azag");

  const handlePhotoUpload = ({ fileList }) => {
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj || fileList[0];
      const reader = new FileReader();
      reader.onload = (e) => setPhotoBase64(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPhotoBase64(null);
    }
  };

  const handleSubmit = async (values) => {
    if (!userGroups?.length) return;
    const place_id = isAzag ? "azgb43p6m5" : "glra12a4a6";

    const payload = {
      place_id,
      properties: {
        emp_department: values.emp_department,
        emp_position: values.emp_position,
        emp_category: values.emp_category,
        user_gender: values.user_gender,
        user_mobile: values.user_mobile,
        user_date_of_birth: dayjs(values.user_date_of_birth).format(
          "DD.MM.YYYY"
        ),
        start_work_time: values.start_work_time.format("HH:mm"),
        end_work_time: values.end_work_time.format("HH:mm"),
        // license_plate: values.license_plate,
        // emp_type: values.emp_type,
        ...(isAzag && {
          license_plate: values.license_plate,
          emp_type: values.emp_type,
        }),
      },
      user_name: values.user_name,
      user_surname: values.user_surname,
      data_url: [photoBase64],
    };

    try {
      setSubmitting(true);
      await createUserFx(payload);
      message.success("Сотрудник добавлен");
      form.resetFields();
      setPhotoBase64(null);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Добавить сотрудника"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={700}
      bodyStyle={{ paddingBottom: 12, maxHeight: "85vh", overflowY: "auto" }}
    >
      <div className={styles.modalContent}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Фото сотрудника (до 300KB)">
            <Upload
              name="photo"
              listType="picture"
              maxCount={1}
              beforeUpload={(file) => {
                const isValidFormat =
                  file.type === "image/jpeg" || file.type === "image/png";
                if (!isValidFormat) {
                  message.error(
                    "Можно загружать только файлы JPG, JPEG или PNG."
                  );
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={handlePhotoUpload}
              accept=".jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>Загрузить фото</Button>
            </Upload>
          </Form.Item>

          <div className={styles.gridForm}>
            <Form.Item
              name="user_name"
              label="Имя"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="user_surname"
              label="Фамилия"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="emp_department"
              label="Департамент"
              rules={[{ required: true }]}
            >
              <Select allowClear showSearch optionFilterProp="children">
                <Option value="N/A">Неизвестно</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="emp_position"
              label="Должность"
              rules={[{ required: true }]}
            >
              <Input placeholder="Введите должности через запятую" />
            </Form.Item>

            <Form.Item
              name="emp_category"
              label="Статус"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="Сотрудник">Сотрудник</Option>
                <Option value="Уволен">Уволен</Option>
              </Select>
            </Form.Item>
            <Form.Item name="user_date_of_birth" label="Дата рождения">
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="user_mobile"
              label="Номер телефона"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="start_work_time" label="Начало рабочего дня">
              <TimePicker format="HH:mm" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="end_work_time" label="Конец рабочего дня">
              <TimePicker format="HH:mm" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="user_gender" label="Пол">
              <Radio.Group>
                <Radio value="Male">М</Radio>
                <Radio value="Female">Ж</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          {isAzag && (
            <div className={styles.gridForm}>
              <Form.Item name="license_plate" label="Гос. номер машины">
                <Input />
              </Form.Item>
              <Form.Item name="emp_type" label="Тип сотрудника">
                <Select>
                  <Option value="Штатный">Штатный</Option>
                  <Option value="Наемный">Наемный</Option>
                </Select>
              </Form.Item>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Сохранить
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
